import moment from "moment";
import { useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import { match as MatchProps } from "react-router";
import { Box, Typography } from "@mui/material";

import { push } from "connected-react-router";
import List from "../../../components/List";
import { t } from "../../../components/translate";
import { useAppSelector } from "../../../store/hooks";
import PageControl from "../../../components/PageControl";
import { notificationAdd } from "../../../actions/notification";
import { getMinStockProducts } from "../../../api/stockProducts";
import useKitchenPharmacy from "../../../hooks/useKitchenPharmacy";
import { StockProducts } from "../../../interfaces/StockInterfaces";
import {
  expirySort,
  exposeKeys,
  getStyleBasedOnExpiryDate,
  getTotalQuantity,
  multipleHeaders
} from "../StockList";

import "./PurchaseOrderDraft.css";

interface PropType {
  match: MatchProps;
}

const pageSize = 50;

export default function PurchaseOrderDraft({ match }: PropType): React.ReactNode {
  const dispatch = useDispatch();
  const [page, setPage] = React.useState(0);
  const [minStockProducts, setMinStockProducts] = useState<{
    results: StockProducts[];
    total: number;
  }>({ results: [], total: 0 });

  const isRcKitchenPharmacy = Boolean(useKitchenPharmacy());
  const isCentralStore =
    useAppSelector((state) => state.userContext.resourceCentre?.isKitchenPharmacy) || false;

  useEffect(() => {
    (async () => {
      try {
        const res = await getMinStockProducts({ page, pageSize });
        if (res.total > 0) {
          setMinStockProducts(res);
        }
      } catch (err) {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: err?.message || err?.data?.message || "Something Went Wrong!.",
            autoTimeout: true
          })
        );
      }
    })();
  }, [page, dispatch]);

  return (
    <Box sx={{ height: "100%" }} className="purchaseOrderDraftList">
      <List
        data={exposeKeys(minStockProducts?.results || []) || []}
        withoutSearch
        rowHeight={40}
        multipleHeaders={multipleHeaders(match, isRcKitchenPharmacy, isCentralStore)}
        hideCreateButton
        onRowClick={(product) => {
          dispatch(push(`/stock/purchase?productId=${product?.id}`));
        }}
        columns={[
          {
            key: "name",
            label: `${t("Stock.name")}`,
            sortable: true,
            formatter: ({ name, structuredInfo }) => (
              <Box display="flex" flexDirection="column">
                <Typography>{(name || "").split("", 50).join("")}</Typography>
                {structuredInfo?.genericName && (
                  <Typography style={{ fontSize: "0.65rem", color: "grey" }}>
                    {structuredInfo.genericName}
                  </Typography>
                )}
              </Box>
            )
          },
          {
            key: "productCode",
            label: `${t("Stock.ProductCode")}`,
            hideInNarrowView: true,
            formatter: ({ productCode }) => <Typography>{productCode || "-"}</Typography>
          },
          {
            key: "minStockQuantity",
            label: `${t("Stock.MinStockQuantity")}`,
            formatter: ({ minStockQuantity }) => <Typography>{minStockQuantity || "-"}</Typography>
          },
          {
            key: "quantity",
            label: `${t("Stock.quantity")}`,
            formatter: ({ stocks }) => (
              <Typography sx={{ color: "red" }}>{getTotalQuantity(stocks)}</Typography>
            )
          },
          {
            key: "unit",
            label: `${t("Stock.unit/package")}`,
            formatter: ({ unit, package: p, unitsPerPackage }) => (
              <Typography>{`${unitsPerPackage} ${unit}/${p} `}</Typography>
            )
          },
          {
            key: "expiryDate",
            label: `${t("Stock.ExpiryDate")}`,
            sortable: true,
            sortFunction: expirySort,
            formatter: ({ expiryDate }) => (
              <Typography style={getStyleBasedOnExpiryDate(expiryDate)}>
                {expiryDate ? moment(expiryDate).format("LL") : "-"}
              </Typography>
            )
          }
        ]}
      />

      <Box className="navigatePage">
        <PageControl
          page={page}
          onSetPage={(val) => {
            setPage(val);
          }}
          pageSize={pageSize}
          maximumDataCount={minStockProducts.total || 0}
        />
      </Box>
    </Box>
  );
}
